import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const CapabilityDemo = () => {
  const [activeCapability, setActiveCapability] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });

  const capabilities = [
    {
      id: 1,
      title: "3D Web Experiences",
      description: "Immersive three-dimensional interfaces that engage users and create memorable digital experiences through WebGL and Three.js technology.",
      icon: "Box",
      color: "from-blue-500 to-cyan-500",
      features: ["WebGL Rendering", "3D Animations", "Interactive Models", "VR/AR Ready"],
      demo: {
        type: "3d-cube",
        elements: 8
      }
    },
    {
      id: 2,
      title: "Real-time Data Visualization",
      description: "Dynamic charts and graphs that transform complex data into intuitive visual stories with smooth animations and interactive elements.",
      icon: "BarChart3",
      color: "from-green-500 to-emerald-500",
      features: ["Live Updates", "Interactive Charts", "Custom Dashboards", "Data Analytics"],
      demo: {
        type: "chart-animation",
        elements: 6
      }
    },
    {
      id: 3,
      title: "Motion Design Systems",
      description: "Sophisticated animation frameworks that bring interfaces to life with purposeful motion, micro-interactions, and delightful user feedback.",
      icon: "Zap",
      color: "from-purple-500 to-pink-500",
      features: ["Micro-interactions", "Page Transitions", "Loading Animations", "Gesture Support"],
      demo: {
        type: "motion-waves",
        elements: 12
      }
    },
    {
      id: 4,
      title: "Responsive Architecture",
      description: "Adaptive layouts that provide optimal viewing experiences across all devices, from desktop workstations to mobile phones.",
      icon: "Monitor",
      color: "from-orange-500 to-red-500",
      features: ["Mobile-First", "Flexible Grids", "Touch Optimized", "Performance Focused"],
      demo: {
        type: "responsive-grid",
        elements: 9
      }
    }
  ];

  const handleCapabilityChange = (index) => {
    if (index !== activeCapability) {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveCapability(index);
        setIsAnimating(false);
      }, 300);
    }
  };

  const renderDemo = (demo) => {
    switch (demo?.type) {
      case '3d-cube':
        return (
          <div className="relative w-full h-64 flex items-center justify-center">
            {[...Array(demo?.elements)]?.map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-8 h-8 bg-gradient-to-br from-accent/60 to-accent/30 rounded-lg"
                animate={{
                  rotateX: [0, 360],
                  rotateY: [0, 360],
                  x: Math.cos(i * 45) * 60,
                  y: Math.sin(i * 45) * 60,
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  delay: i * 0.2,
                  ease: "linear"
                }}
                style={{
                  transformStyle: 'preserve-3d'
                }}
              />
            ))}
          </div>
        );
      
      case 'chart-animation':
        return (
          <div className="relative w-full h-64 flex items-end justify-center space-x-2">
            {[...Array(demo?.elements)]?.map((_, i) => (
              <motion.div
                key={i}
                className="bg-gradient-to-t from-accent to-accent/60 rounded-t-lg"
                style={{ width: '20px' }}
                animate={{
                  height: [20, Math.random() * 150 + 50, 20],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: i * 0.2,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        );
      
      case 'motion-waves':
        return (
          <div className="relative w-full h-64 flex items-center justify-center">
            {[...Array(demo?.elements)]?.map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-4 h-4 bg-accent/40 rounded-full"
                animate={{
                  scale: [0.5, 1.5, 0.5],
                  opacity: [0.3, 0.8, 0.3],
                  x: Math.cos(i * 30) * (50 + i * 10),
                  y: Math.sin(i * 30) * (50 + i * 10),
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: i * 0.1,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        );
      
      case 'responsive-grid':
        return (
          <div className="relative w-full h-64 p-4">
            <div className="grid grid-cols-3 gap-2 h-full">
              {[...Array(demo?.elements)]?.map((_, i) => (
                <motion.div
                  key={i}
                  className="bg-gradient-to-br from-accent/40 to-accent/20 rounded-lg"
                  animate={{
                    scale: [1, 1.05, 1],
                    opacity: [0.6, 1, 0.6],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.15,
                    ease: "easeInOut"
                  }}
                />
              ))}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <section ref={ref} className="py-20 bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Our <span className="text-accent">Capabilities</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Interactive demonstrations of our technical expertise and creative solutions that bring digital experiences to life.
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Capability Selector */}
            <div className="space-y-4">
              {capabilities?.map((capability, index) => (
                <motion.div
                  key={capability?.id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`cursor-pointer p-6 rounded-2xl border transition-all duration-500 ${
                    activeCapability === index
                      ? 'bg-gradient-to-r from-accent/10 to-accent/5 border-accent/30 shadow-accent'
                      : 'bg-card/50 border-border/50 hover:bg-card/80 hover:border-border'
                  }`}
                  onClick={() => handleCapabilityChange(index)}
                >
                  <div className="flex items-start space-x-4">
                    <motion.div
                      animate={activeCapability === index ? {
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, 0]
                      } : {}}
                      transition={{ duration: 0.6 }}
                      className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        activeCapability === index
                          ? `bg-gradient-to-br ${capability?.color} shadow-lg`
                          : 'bg-muted'
                      }`}
                    >
                      <Icon 
                        name={capability?.icon} 
                        size={24} 
                        color={activeCapability === index ? "white" : "var(--color-muted-foreground)"}
                      />
                    </motion.div>

                    <div className="flex-1">
                      <h3 className={`text-xl font-bold mb-2 transition-colors ${
                        activeCapability === index ? 'text-accent' : 'text-foreground'
                      }`}>
                        {capability?.title}
                      </h3>
                      <p className="text-muted-foreground mb-4 leading-relaxed">
                        {capability?.description}
                      </p>

                      {/* Features */}
                      <div className="flex flex-wrap gap-2">
                        {capability?.features?.map((feature, featureIndex) => (
                          <motion.span
                            key={featureIndex}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={activeCapability === index ? { opacity: 1, scale: 1 } : { opacity: 0.6, scale: 0.9 }}
                            transition={{ delay: featureIndex * 0.1 }}
                            className={`text-xs font-medium px-2 py-1 rounded-md transition-all ${
                              activeCapability === index
                                ? 'bg-accent/20 text-accent border border-accent/30' :'bg-muted/50 text-muted-foreground'
                            }`}
                          >
                            {feature}
                          </motion.span>
                        ))}
                      </div>
                    </div>

                    {/* Active Indicator */}
                    {activeCapability === index && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-3 h-3 bg-accent rounded-full"
                      />
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Demo Area */}
            <div className="relative">
              <motion.div
                key={activeCapability}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: isAnimating ? 0 : 1, scale: isAnimating ? 0.9 : 1 }}
                transition={{ duration: 0.5 }}
                className={`relative bg-gradient-to-br ${capabilities?.[activeCapability]?.color}/10 backdrop-blur-sm border border-border/50 rounded-3xl p-8 shadow-3d overflow-hidden`}
              >
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, var(--color-accent) 1px, transparent 0)`,
                    backgroundSize: '30px 30px',
                  }}></div>
                </div>

                {/* Demo Content */}
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-2xl font-bold text-foreground">
                      {capabilities?.[activeCapability]?.title}
                    </h4>
                    <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${capabilities?.[activeCapability]?.color} animate-pulse`}></div>
                  </div>

                  {/* Interactive Demo */}
                  <div className="perspective-1000">
                    {renderDemo(capabilities?.[activeCapability]?.demo)}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CapabilityDemo;